/**
 * Spécification menu ↔ permissions nav.* (matrice + sidebar).
 * Visibilité menu = codes nav.* uniquement (pas de case.view / production.view).
 */
import {
  OPERATOR_FAMILY_ROLE_TYPES,
  PRODUCTION_MANAGER_ROLE_TYPES,
  WORKSHOP_ATELIER_ROLE_TYPES,
} from './operatorRoles';

/** Codes nav feuilles (ordre matrice 1 → 10.5). */
export const MENU_LEAF_PERMISSION_CODES = [
  'nav.dashboard',
  'nav.patients',
  'nav.cases',
  'nav.dentist_billing_pack',
  'nav.training',
  'nav.production.tracking',
  'nav.production.workshop',
  'nav.billing.invoices',
  'nav.billing.config',
  'nav.payment.schedule',
  'nav.payment.overview',
  'nav.payment.discounts',
  'nav.admin.users',
  'nav.admin.workflow',
  'nav.admin.packs_services',
  'nav.admin.machines',
  'nav.admin.countries',
  'nav.admin.integrations',
  'nav.admin.platform_messages',
];

/** Routes admin / facturation / paiement (garde ProtectedRoute). */
export const ADMIN_ROUTE_NAV_PERMISSION_CODES = MENU_LEAF_PERMISSION_CODES.filter(
  (c) =>
    c.startsWith('nav.admin.') ||
    c.startsWith('nav.billing.') ||
    c.startsWith('nav.payment.'),
);

/**
 * Alias minimal : uniquement migration nav.home → nav.dashboard.
 * Ne pas y mettre production.view / case.view (sinon menu élargi à tort).
 */
export const NAV_PERMISSION_LEGACY_ALIASES = {
  'nav.dashboard': ['nav.home'],
};

/** Codes retirés de la matrice et des profils menu. */
export const NAV_LEGACY_READ_ONLY_CODES = [
  'nav.home',
  'nav.pack_user',
  'nav.workshop_operator',
  'production.view',
  'production.manage',
  'user.view',
  'admin.platform',
  'user.assign_roles',
  'case.view',
  'case.create',
  'case.edit',
  'case.assign',
  'case.delete',
  'billing.view',
];

export const MENU_MATRIX_DISPLAY_ROWS = [
  { kind: 'permission', code: 'nav.dashboard', indent: 0 },
  { kind: 'permission', code: 'nav.patients', indent: 0 },
  { kind: 'permission', code: 'nav.cases', indent: 0 },
  { kind: 'permission', code: 'nav.dentist_billing_pack', indent: 0 },
  { kind: 'permission', code: 'nav.training', indent: 0 },
  { kind: 'section', labelFr: 'Production', labelEn: 'Production' },
  { kind: 'permission', code: 'nav.production.tracking', indent: 1 },
  { kind: 'permission', code: 'nav.production.workshop', indent: 1 },
  { kind: 'section', labelFr: 'Facturation', labelEn: 'Billing' },
  { kind: 'permission', code: 'nav.billing.invoices', indent: 1 },
  { kind: 'permission', code: 'nav.billing.config', indent: 1 },
  { kind: 'section', labelFr: 'Paiement', labelEn: 'Payment' },
  { kind: 'permission', code: 'nav.payment.schedule', indent: 1 },
  { kind: 'permission', code: 'nav.payment.overview', indent: 1 },
  { kind: 'permission', code: 'nav.payment.discounts', indent: 1 },
  { kind: 'section', labelFr: 'Administration', labelEn: 'Administration' },
  { kind: 'permission', code: 'nav.admin.users', indent: 1 },
  { kind: 'permission', code: 'nav.admin.workflow', indent: 1 },
  { kind: 'permission', code: 'nav.admin.packs_services', indent: 1 },
  { kind: 'permission', code: 'nav.admin.machines', indent: 1 },
  { kind: 'permission', code: 'nav.admin.countries', indent: 1 },
  { kind: 'permission', code: 'nav.admin.integrations', indent: 1 },
  { kind: 'permission', code: 'nav.admin.platform_messages', indent: 1 },
];

export function codesIncludingNavAliases(codes) {
  const set = new Set();
  for (const code of codes || []) {
    if (!code) continue;
    set.add(code);
    for (const alias of NAV_PERMISSION_LEGACY_ALIASES[code] || []) {
      set.add(alias);
    }
  }
  return [...set];
}

export function navPermissions(...codes) {
  return codesIncludingNavAliases(codes);
}

const PROD_TRACKING_ROLES = [
  'administrator',
  ...PRODUCTION_MANAGER_ROLE_TYPES,
  ...WORKSHOP_ATELIER_ROLE_TYPES,
];
const WORKSHOP_ROLES = ['administrator', ...PRODUCTION_MANAGER_ROLE_TYPES, ...WORKSHOP_ATELIER_ROLE_TYPES];

export const MENU_SIDEBAR_LEAF_CONFIG = {
  'nav.dashboard': {
    pathKey: 'dashboard',
    roles: [
      'administrator',
      'dentist_client',
      'dentist_supplier',
      'agent_administratif',
      ...PRODUCTION_MANAGER_ROLE_TYPES,
      ...WORKSHOP_ATELIER_ROLE_TYPES,
    ],
  },
  'nav.patients': {
    path: '/patients-list',
    roles: ['administrator', 'dentist_client', 'agent_administratif'],
  },
  'nav.cases': {
    path: '/cases-list',
    roles: [
      'administrator',
      'dentist_client',
      'dentist_supplier',
      'agent_administratif',
      ...OPERATOR_FAMILY_ROLE_TYPES,
    ],
  },
  'nav.dentist_billing_pack': {
    path: '/dentiste/facturation-pack',
    roles: ['dentist_client'],
  },
  'nav.training': {
    path: '/training-videos-library',
    roles: ['administrator', 'dentist_client'],
  },
  'nav.production.tracking': {
    path: '/production-tracking-dashboard',
    roles: PROD_TRACKING_ROLES,
  },
  'nav.production.workshop': {
    path: '/atelier-lot',
    roles: WORKSHOP_ROLES,
  },
  'nav.billing.invoices': { path: '/dentist-invoices-tracking', roles: ['administrator'] },
  'nav.billing.config': { path: '/billing-phases-management', roles: ['administrator'] },
  'nav.payment.schedule': { path: '/advanced-payment-configuration', roles: ['administrator'] },
  'nav.payment.overview': { path: '/billing-overview', roles: ['administrator'] },
  'nav.payment.discounts': { path: '/advanced-billing-management', roles: ['administrator'] },
  'nav.admin.users': { path: '/admin-users-management', roles: ['administrator'] },
  'nav.admin.workflow': { path: '/phase-templates', roles: ['administrator'] },
  'nav.admin.packs_services': { path: '/admin/packs-services', roles: ['administrator'] },
  'nav.admin.machines': { path: '/machine-fleet-management', roles: ['administrator'] },
  'nav.admin.countries': { path: '/international-market-management', roles: ['administrator'] },
  'nav.admin.integrations': { path: '/admin/integrations', roles: ['administrator'] },
  'nav.admin.platform_messages': { path: '/admin/platform-messages', roles: ['administrator'] },
};

